import { Component, computed, effect, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PendingActivationResponse } from 'dashboard/components/visuals-dashboard/widgets/pending-activation-widget/pending-activation-data-response.interface';
import { IceDashboardDisplayStateEnum } from 'dashboard/enums/visuals/ice-dashboard-display-state.enum';
import { VisualsDashboardWidgetSizingEnum } from 'dashboard/enums/visuals/visuals-dashboard-widget-sizing.enum';
import { DashboardWidget } from 'dashboard/interfaces/visuals/dashboard-widget.interface';
import { VisualsDashboardDataService } from 'dashboard/services/visuals-dashboard/visualds-dashboard-data.service';
import { VisualsDashboardService } from 'dashboard/services/visuals-dashboard/visuals-dashboard.service';
import { IceVisualsDashboardLargeNumberDisplayComponent } from 'shared/components/ice-visuals-dashboard/ice-visuals-dashboard-large-number-display/ice-visuals-dashboard-large-number-display.component';
import {
    IceVisualsDashboardTableDisplayComponent,
    TableColumn,
} from 'shared/components/ice-visuals-dashboard/ice-visuals-dashboard-table-display/ice-visuals-dashboard-table-display.component';

interface ListingData {
    id: string;
    name: string;
}

@Component({
    selector: 'app-pending-activation-widget',
    imports: [IceVisualsDashboardTableDisplayComponent, IceVisualsDashboardLargeNumberDisplayComponent, TranslateModule],
    templateUrl: './pending-activation-widget.component.html',
    styleUrl: './pending-activation-widget.component.scss',
    providers: [VisualsDashboardDataService],
})
export class PendingActivationWidgetComponent {
    widget = input.required<DashboardWidget>();
    visualsDashboardWidgetSizingEnum = VisualsDashboardWidgetSizingEnum;
    iceDashboardDisplayStateEnum = IceDashboardDisplayStateEnum;

    onLangChange = toSignal(this.translateService.onLangChange);

    widgetId = computed(() => {
        const widget = this.widget();
        return widget.iceDashboardWidget.widgetId;
    });

    columns: TableColumn[] = [
        { key: 'id', translationKey: 'dashboard.visuals.widgets.pendingActivation.table.columns.id', width: '30%' },
        { key: 'name', translationKey: 'dashboard.visuals.widgets.pendingActivation.table.columns.name', width: '70%' },
    ];

    totalListings = computed(() => {
        const widgetData = this.dataService.data();
        if (!widgetData) {
            return 0;
        }
        return widgetData.totalListings;
    });

    selectedSize = computed(() => {
        const widget = this.widget();
        return widget.selectedSize;
    });

    listings = computed<ListingData[]>(() => {
        const widgetData = this.dataService.data();
        this.onLangChange(); // Re-compute when language changes
        if (!widgetData || !widgetData.ranges) {
            return [];
        }
        const listingsArray: ListingData[] = [];
        widgetData.ranges.forEach((range) => {
            const item: ListingData = {
                id: range.mappedId,
                name: range.title,
            };
            listingsArray.push(item);
        });
        return listingsArray;
    });

    widgetState = computed(() => {
        const widgetData = this.dataService.data();
        if (!widgetData) {
            return null;
        }
        return widgetData.totalListings === 0 ? IceDashboardDisplayStateEnum.SUCCESS : IceDashboardDisplayStateEnum.WARNING;
    });

    constructor(
        public dataService: VisualsDashboardDataService<PendingActivationResponse>,
        private visualsDashboardService: VisualsDashboardService,
        private translateService: TranslateService,
    ) {
        effect(() => {
            const widgetId = this.widgetId();
            this.dataService.load(widgetId);
        });
        effect(() => {
            const state = this.widgetState();
            const widgetHtmlId = this.widget().htmlId;
            if (widgetHtmlId && state !== null) {
                this.visualsDashboardService.setHtmlIdBackgroundStateOverride(widgetHtmlId, state);
            }
        });
    }
}
